import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { CheckCircle2, AlertTriangle, XCircle, Clock, RefreshCw } from "lucide-react";

// Os 9 invariantes do sentinel-smoke-cron
const INVARIANT_LIST = [
  { id: 'I-1', name: 'Author type = assistant', description: '≥99% dos turnos do bot têm author_type=assistant em syra_messages' },
  { id: 'I-2', name: 'Dual-write WM consistente', description: 'Divergência legacy↔v18 ≤2% em 24h' },
  { id: 'I-3', name: 'Skill OS v20 runtime', description: 'Quando flag ON, ≥1 evento skill.runtime.* por hora ativa' },
  { id: 'I-4', name: 'Affect snapshot fresco', description: 'affect_states.updated_at ≤30min em grupos ativos' },
  { id: 'I-5', name: 'ToM modelo fresco', description: 'tom_models.updated_at ≤60min em grupos ativos' },
  { id: 'I-6', name: 'Latência p95 do turno', description: 'p95 dos turnos da última 1h ≤8000ms' },
  { id: 'I-7', name: 'Sem loop ask_user', description: 'Zero participantes com 3+ ask_user consecutivos em 7d' },
  { id: 'I-8', name: 'Cortex master flag ON', description: 'cortex_full_loop_enabled deve estar enabled=true' },
  { id: 'I-9', name: 'Truncation ratio (6h)', description: 'Pico de truncamento em 6h ≤5% (warn) / ≤15% (critical) — view truncation_ratio_view' },
];

type Status = 'healthy' | 'warning' | 'critical' | 'stale';

interface InvariantState {
  id: string;
  name: string;
  description: string;
  status: Status;
  last_alert_at: string | null;
  last_alert_severity: string | null;
  last_alert_payload: any;
}

const STATUS_META: Record<Status, { color: string; bg: string; label: string; Icon: typeof CheckCircle2 }> = {
  healthy: { color: 'text-green-600', bg: 'bg-green-50 border-green-200', label: 'Saudável', Icon: CheckCircle2 },
  warning: { color: 'text-yellow-600', bg: 'bg-yellow-50 border-yellow-200', label: 'Atenção', Icon: AlertTriangle },
  critical: { color: 'text-red-600', bg: 'bg-red-50 border-red-200', label: 'Crítico', Icon: XCircle },
  stale: { color: 'text-gray-500', bg: 'bg-gray-50 border-gray-200', label: 'Sem leitura', Icon: Clock },
};

export default function HealthDashboard() {
  const [states, setStates] = useState<InvariantState[]>([]);
  const [loading, setLoading] = useState(true);
  const [lastRunAt, setLastRunAt] = useState<string | null>(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    const { data: lastRun } = await supabase
      .from('sentinel_smoke_alerts')
      .select('created_at')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    setLastRunAt(lastRun?.created_at ?? null);

    const lastRunMs = lastRun?.created_at ? new Date(lastRun.created_at).getTime() : 0;
    const hoursSinceLastRun = lastRun ? (Date.now() - lastRunMs) / (60 * 60 * 1000) : Infinity;

    const promises = INVARIANT_LIST.map(async (inv): Promise<InvariantState> => {
      const { data: alerts } = await supabase
        .from('sentinel_smoke_alerts')
        .select('severity, status, payload, created_at')
        .eq('invariant_id', inv.id)
        .gte('created_at', new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString())
        .order('created_at', { ascending: false })
        .limit(1);

      const latest = alerts?.[0];
      let status: Status = 'healthy';
      if (!lastRun || hoursSinceLastRun > 24) {
        status = 'stale';
      } else if (latest && latest.status === 'open') {
        status = latest.severity === 'critical' ? 'critical' : 'warning';
      }

      return {
        id: inv.id,
        name: inv.name,
        description: inv.description,
        status,
        last_alert_at: latest?.created_at ?? null,
        last_alert_severity: latest?.severity ?? null,
        last_alert_payload: latest?.payload ?? null,
      };
    });

    const results = await Promise.all(promises);
    setStates(results);
    setLoading(false);
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const healthyCount = states.filter(s => s.status === 'healthy').length;
  const totalCount = states.length;
  const overallVariant: 'default' | 'secondary' | 'destructive' =
    healthyCount === totalCount ? 'default' :
    healthyCount >= 4 ? 'secondary' : 'destructive';

  const lastRunLabel = (() => {
    if (!lastRunAt) return 'nunca';
    const minsAgo = Math.floor((Date.now() - new Date(lastRunAt).getTime()) / 60000);
    if (minsAgo < 60) return `${minsAgo}min atrás`;
    const hoursAgo = Math.floor(minsAgo / 60);
    return `${hoursAgo}h atrás`;
  })();

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold">Saúde da SYRA</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Última execução do Sentinel: {lastRunLabel}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={overallVariant} className="text-sm px-3 py-1">
            {healthyCount}/{totalCount} saudáveis
          </Badge>
          <Button variant="outline" size="sm" onClick={fetchAll} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 9 }).map((_, i) => (
            <Skeleton key={i} className="h-40" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {states.map((s) => {
            const meta = STATUS_META[s.status];
            const Icon = meta.Icon;
            return (
              <Card key={s.id} className={`border-2 ${meta.bg}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base font-mono">{s.id}</CardTitle>
                    <Icon className={`h-5 w-5 ${meta.color}`} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <p className={`text-xs font-semibold uppercase tracking-wide ${meta.color}`}>{meta.label}</p>
                  <p className="text-sm font-medium">{s.name}</p>
                  <p className="text-xs text-muted-foreground leading-snug">{s.description}</p>
                  {s.id === 'I-9' && typeof s.last_alert_payload?.metric_value === 'number' && (
                    <p className={`text-sm font-mono font-semibold ${meta.color}`}>
                      Pico: {Number(s.last_alert_payload.metric_value).toFixed(2)}%
                    </p>
                  )}
                  {s.last_alert_at && (
                    <p className="text-[11px] text-muted-foreground pt-1 border-t">
                      Último alerta: {new Date(s.last_alert_at).toLocaleString('pt-BR')}
                    </p>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
